import React, { useContext, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Fade,
  Slide,
  Menu,
  MenuItem,
  Collapse,
  Tooltip,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Payment as PaymentIcon,
  AdminPanelSettings as AdminIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  AttachMoney as FinanceIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WarningAmber as WarningAmberIcon,
  AssignmentReturn as AssignmentReturnIcon,
  AccountBalance as CreditIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ThemeModeContext } from '../contexts/ThemeModeContext';

const drawerWidth = 272;
const collapsedDrawerWidth = 72;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { toggleMode } = useContext(ThemeModeContext);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const currentDrawerWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  const handleCollapseToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  const isDarkMode = theme.palette.mode === 'dark';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setAnchorEl(null);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Cobranzas', icon: <PaymentIcon />, path: '/collections' },
    ...(user?.role === 'admin'
      ? [{ text: 'Gestión', icon: <AdminIcon />, path: '/admin' }]
      : []),
    ...(user?.role === 'admin'
      ? [{ text: 'Morosos', icon: <WarningAmberIcon />, path: '/morosos' }]
      : []),
    ...(user?.role === 'admin'
      ? [{ text: 'Devoluciones', icon: <AssignmentReturnIcon />, path: '/returns' }]
      : []),
    ...(user?.role === 'admin'
      ? [{ text: 'Finanzas', icon: <FinanceIcon />, path: '/finance' }]
      : []),
    ...(user?.role === 'admin'
      ? [{ text: 'Créditos', icon: <CreditIcon />, path: '/creditos' }]
      : []),
    ...(user?.role === 'cobrador'
      ? []
      : [{ text: 'Seguimiento de Ventas', icon: <TrendingUpIcon />, path: '/sales-tracking' }]),
  ];

  if (user?.role === 'admin') {
    menuItems.push({ text: 'Logs de Auditoria', icon: <HistoryIcon />, path: '/audit-logs' });
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: collapsed ? 1.5 : 3, pb: collapsed ? 1.5 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1rem' }}>
              SC
            </Typography>
          </Box>
          {!collapsed && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  letterSpacing: '-0.01em',
                }}
              >
                Cobranzas
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              >
                Gestion de Pagos
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Divider sx={{ mx: collapsed ? 1 : 2, opacity: 0.5 }} />

      {/* Seccion label */}
      {!collapsed && (
        <Typography
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            opacity: 0.6,
          }}
        >
          Menu principal
        </Typography>
      )}
      {collapsed && <Box sx={{ pt: 1.5 }} />}

      {/* Navigation */}
      <List sx={{ flex: 1, px: collapsed ? 0.5 : 1, py: 0 }}>
        {menuItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          const button = (
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  py: 1.25,
                  px: collapsed ? 0 : 2,
                  borderRadius: 3,
                  mx: collapsed ? 0.5 : 1,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  minHeight: 44,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  ...(isActive
                    ? {
                        backgroundColor: isDarkMode
                          ? 'rgba(99, 102, 241, 0.15)'
                          : '#6366F1',
                        boxShadow: isDarkMode
                          ? 'none'
                          : '0 4px 12px rgba(99, 102, 241, 0.3)',
                        '&:hover': {
                          backgroundColor: isDarkMode
                            ? 'rgba(99, 102, 241, 0.2)'
                            : '#4F46E5',
                        },
                      }
                    : {
                        '&:hover': {
                          backgroundColor: isDarkMode
                            ? 'rgba(255, 255, 255, 0.04)'
                            : 'rgba(0, 0, 0, 0.03)',
                        },
                      }),
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 36,
                    mr: collapsed ? 0 : undefined,
                    justifyContent: 'center',
                    color: isActive
                      ? isDarkMode
                        ? '#A5B4FC'
                        : '#ffffff'
                      : 'text.secondary',
                  }}
                >
                  {React.cloneElement(item.icon as React.ReactElement, { sx: { fontSize: 20 } })}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive
                          ? isDarkMode
                            ? '#E0E7FF'
                            : '#ffffff'
                          : 'text.primary',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
          return (
            <Fade in timeout={200 + index * 60} key={item.text}>
              {collapsed ? (
                <Tooltip title={item.text} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </Fade>
          );
        })}
      </List>

      <Divider sx={{ mx: collapsed ? 1 : 2, opacity: 0.5 }} />

      {/* Collapse toggle button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Tooltip title={collapsed ? 'Expandir menu' : 'Colapsar menu'} placement="right">
          <IconButton
            onClick={handleCollapseToggle}
            size="small"
            sx={{
              width: 32,
              height: 32,
              color: 'text.secondary',
              backgroundColor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: isDarkMode
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* User section */}
      <Box sx={{ p: collapsed ? 1 : 2 }}>
        {collapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Tooltip title={`${user?.username} (${user?.role})`} placement="right">
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
            <Tooltip title="Cerrar Sesion" placement="right">
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: isDarkMode
                      ? 'rgba(239, 68, 68, 0.1)'
                      : 'rgba(239, 68, 68, 0.06)',
                    color: '#EF4444',
                  },
                }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                borderRadius: 3,
                backgroundColor: isDarkMode
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.02)',
                mb: 1.5,
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  mr: 1.5,
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'capitalize',
                    fontSize: '0.72rem',
                  }}
                >
                  {user?.role}
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              size="small"
              startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
              onClick={handleLogout}
              sx={{
                borderRadius: 2.5,
                py: 1,
                color: 'text.secondary',
                fontSize: '0.8rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(239, 68, 68, 0.1)'
                    : 'rgba(239, 68, 68, 0.06)',
                  color: '#EF4444',
                },
              }}
            >
              Cerrar Sesion
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  // Mobile menu
  const MobileMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: '100vw',
          maxWidth: '100vw',
          left: '0 !important',
          right: '0 !important',
          top: '64px !important',
          borderRadius: 0,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: 'none',
          maxHeight: 'calc(100vh - 64px)',
          overflow: 'auto',
        },
      }}
    >
      <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 1,
          }}
        >
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.9rem' }}>SC</Typography>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
          Sistema de Cobranzas
        </Typography>
      </Box>

      {menuItems.map((item) => (
        <MenuItem
          key={item.path}
          onClick={() => {
            navigate(item.path);
            handleMobileMenuClose();
          }}
          sx={{
            py: 1.5,
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: location.pathname === item.path ? 'primary.main' : 'text.secondary' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: location.pathname === item.path ? 600 : 400,
              color: location.pathname === item.path ? 'primary.main' : 'text.primary',
              fontSize: '0.9rem',
            }}
          />
        </MenuItem>
      ))}

      <Divider />
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {user?.username}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
          {user?.role}
        </Typography>
      </Box>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button
          fullWidth
          size="small"
          startIcon={<LogoutIcon />}
          onClick={() => {
            handleLogout();
            handleMobileMenuClose();
          }}
          sx={{
            py: 1,
            color: '#EF4444',
            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.06)' },
          }}
        >
          Cerrar Sesion
        </Button>
      </Box>
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { lg: `${currentDrawerWidth}px` },
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: isDarkMode
            ? 'rgba(12, 15, 26, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(16px)',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          maxWidth: '100vw',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, minHeight: { xs: 60, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={isMobile ? handleMobileMenuToggle : handleDrawerToggle}
            sx={{
              mr: 2,
              display: { lg: 'none' },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <Typography
              noWrap
              component="div"
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                color: 'text.primary',
                minWidth: 0,
              }}
            >
              Bienvenido, <Box component="span" sx={{ color: 'primary.main' }}>{user?.username}</Box>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Tooltip title={isDarkMode ? 'Modo claro' : 'Modo oscuro'}>
              <IconButton
                onClick={toggleMode}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  color: 'text.secondary',
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: isDarkMode
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                {isDarkMode ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  textTransform: 'capitalize',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {user?.role}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { lg: currentDrawerWidth }, flexShrink: { lg: 0 }, transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        <Drawer
          variant="temporary"
          open={false}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: 'none' }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              backgroundColor: isDarkMode ? '#0c0f1a' : '#ffffff',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          pb: { xs: '88px', lg: 3 },
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          maxWidth: '100vw',
          overflow: 'hidden',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, sm: 64 } }} />
        <Fade in timeout={400}>
          <Box>
            <Outlet />
          </Box>
        </Fade>
      </Box>

      {isMobile && <MobileMenu />}

      {/* Bottom Navigation - mobile only */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', lg: 'none' },
          zIndex: theme.zIndex.appBar,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        elevation={8}
      >
        <BottomNavigation
          value={location.pathname}
          onChange={(_, newValue) => navigate(newValue)}
          showLabels
          sx={{
            backgroundColor: isDarkMode ? '#0c0f1a' : '#ffffff',
            height: 72,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              py: 1,
              gap: 0.25,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: '#6366F1',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.4rem',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.72rem',
              fontWeight: 500,
              '&.Mui-selected': {
                fontSize: '0.72rem',
                fontWeight: 700,
              },
            },
          }}
        >
          <BottomNavigationAction label="Inicio" value="/dashboard" icon={<DashboardIcon />} />
          <BottomNavigationAction label="Cobrar" value="/collections" icon={<PaymentIcon />} />
          {user?.role === 'admin' && (
            <BottomNavigationAction label="Admin" value="/admin" icon={<AdminIcon />} />
          )}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
